<template>
  <div :class="standalone ? 'content-page-layout' : 'modal-backdrop'">
    <div class="modal-container content-modal" :class="{ 'content-modal--standalone': standalone }">
      <div class="modal-header content-modal-header" :class="{ 'content-modal-header--standalone': standalone }">
        <button v-if="standalone" type="button" class="content-back-btn" @click="$emit('close')">
          <i class="bi bi-arrow-left"></i>
          <span>Назад</span>
        </button>
        <h5 class="modal-title" :class="{ 'modal-title--standalone': standalone }">Редактирование контента</h5>
        <button v-if="!standalone" type="button" class="modal-close" @click="$emit('close')" aria-label="Close">&times;</button>
      </div>

      <form class="content-modal-form" @submit.prevent="onSave">
        <div class="modal-body final-panel content-modal-body">
          <div v-if="isFormLoading" class="text-muted small">Загружаю форму…</div>

          <template v-else-if="form && form.sections && form.sections.length">
            <div v-for="(section, sectionIndex) in form.sections" :key="section.id || sectionIndex" class="content-section">
              <div v-if="sectionTitle(section, sectionIndex)" class="content-section__head">
                <span class="content-section__title">{{ sectionTitle(section, sectionIndex) }}</span>
              </div>

              <div class="content-blocks-grid">
                <article
                  v-for="field in section.fields"
                  :key="field.name"
                  class="content-edit-block"
                  :class="{
                    'content-edit-block--body': isBodyField(field),
                    'content-edit-block--title': isTitleField(field),
                    'content-edit-block--editing': isEditingField(field.name),
                    'content-edit-block--error': fieldErrors[field.name]
                  }"
                >
                  <div class="content-edit-block__head">
                    <div class="content-edit-block__label-wrap">
                      <span class="content-edit-block__label">
                        {{ field.label }}
                        <span v-if="field.required" class="text-danger">*</span>
                      </span>
                    </div>
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm content-edit-block__edit"
                      :disabled="isSaving"
                      @click="isEditingField(field.name) ? cancelEditField() : startEditField(field)"
                      :aria-label="isEditingField(field.name) ? 'Закрыть редактирование' : 'Редактировать поле'"
                    >
                      <i :class="isEditingField(field.name) ? 'bi bi-x-lg' : 'bi bi-pencil'"></i>
                    </button>
                  </div>

                  <template v-if="isEditingField(field.name)">
                    <div class="content-edit-block__editor">
                      <input
                        v-if="!isBodyField(field) && (field.type === 'string' || field.type === 'slug')"
                        class="form-control form-control-sm"
                        v-model.trim="draftPost[field.name]"
                        :maxlength="field.maxLength || null"
                        :pattern="field.pattern || null"
                        :placeholder="field.label"
                        :name="field.name"
                      />

                      <textarea
                        v-else-if="isBodyField(field) || field.type === 'markdown'"
                        class="form-control content-edit-block__textarea content-edit-block__textarea--markdown"
                        rows="15"
                        v-model="draftPost[field.name]"
                        :name="field.name"
                      ></textarea>

                      <template v-else-if="field.type === 'image'">
                        <div class="border rounded p-2 text-center content-image-preview">
                          <img v-if="draftPost[field.name]" :src="draftPost[field.name]" alt="" class="final-image" />
                          <div v-else class="text-muted small">Картинка не выбрана</div>
                        </div>
                        <input
                          class="form-control form-control-sm mt-2"
                          v-model.trim="draftPost[field.name]"
                          :placeholder="field.label"
                          :name="field.name"
                        />
                      </template>

                      <select v-else-if="field.type === 'select'" class="form-select form-select-sm" v-model="draftPost[field.name]" :name="field.name">
                        <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
                      </select>

                      <textarea
                        v-else-if="field.type === 'json'"
                        class="form-control content-edit-block__textarea"
                        rows="6"
                        :value="listFieldAsText(field.name)"
                        @input="updateListFieldFromText(field.name, $event.target.value)"
                        @blur="commitListFieldText(field.name)"
                        :name="field.name"
                        placeholder="One value per line or comma separated"
                      ></textarea>

                      <input v-else class="form-control form-control-sm" v-model.trim="draftPost[field.name]" :name="field.name" />

                      <div class="content-edit-block__actions">
                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="cancelEditField">Отмена</button>
                        <button type="button" class="btn btn-primary btn-sm" @click="finishEditField(field)">Готово</button>
                      </div>
                    </div>
                  </template>

                  <template v-else>
                    <div v-if="isBodyField(field) || field.type === 'markdown'" class="content-edit-block__markdown markdown-preview" v-html="renderMarkdown(draftPost[field.name])"></div>

                    <div v-else-if="field.type === 'image'" class="content-edit-block__image-view">
                      <img v-if="draftPost[field.name]" :src="draftPost[field.name]" alt="" class="final-image" />
                      <span v-else class="content-edit-block__empty">Картинка не выбрана</span>
                    </div>

                    <div v-else-if="field.type === 'json'" class="content-edit-block__tags">
                      <span v-for="item in parseJsonList(draftPost[field.name])" :key="`${field.name}-${item}`" class="content-edit-block__tag">
                        {{ item }}
                      </span>
                      <span v-if="!parseJsonList(draftPost[field.name]).length" class="content-edit-block__empty">—</span>
                    </div>

                    <p v-else class="content-edit-block__value mb-0">{{ displayFieldValue(field) }}</p>
                  </template>

                  <div v-if="fieldErrors[field.name]" class="text-danger small mt-2">
                    {{ fieldErrors[field.name] }}
                  </div>
                </article>
              </div>
            </div>
          </template>

          <!-- Фолбэк: если формы нет -->
          <template v-else>
            <div class="content-section">
              <div class="content-blocks-grid">
                <article
                  v-for="field in fallbackFields"
                  :key="field.name"
                  class="content-edit-block"
                  :class="{
                    'content-edit-block--body': isBodyField(field),
                    'content-edit-block--title': isTitleField(field),
                    'content-edit-block--editing': isEditingField(field.name)
                  }"
                >
                  <div class="content-edit-block__head">
                    <div class="content-edit-block__label-wrap">
                      <span class="content-edit-block__label">{{ field.label }}</span>
                    </div>
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm content-edit-block__edit"
                      :disabled="isSaving"
                      @click="isEditingField(field.name) ? cancelEditField() : startEditField(field)"
                      :aria-label="isEditingField(field.name) ? 'Закрыть редактирование' : 'Редактировать поле'"
                    >
                      <i :class="isEditingField(field.name) ? 'bi bi-x-lg' : 'bi bi-pencil'"></i>
                    </button>
                  </div>

                  <div v-if="isEditingField(field.name)" class="content-edit-block__editor">
                    <input
                      v-if="field.type === 'string'"
                      type="text"
                      id="modalContentTitle"
                      v-model="draftPost.title"
                      class="form-control"
                      name="title"
                      required
                    />
                    <textarea
                      v-else
                      id="modalContentBody"
                      v-model="draftPost.body"
                      class="form-control content-edit-block__textarea content-edit-block__textarea--markdown"
                      name="body"
                      rows="30"
                    ></textarea>
                    <div class="content-edit-block__actions">
                      <button type="button" class="btn btn-outline-secondary btn-sm" @click="cancelEditField">Отмена</button>
                      <button type="button" class="btn btn-primary btn-sm" @click="finishEditField(field)">Готово</button>
                    </div>
                  </div>

                  <div v-else-if="isBodyField(field)" class="content-edit-block__markdown markdown-preview" v-html="renderMarkdown(draftPost.body)"></div>
                  <p v-else class="content-edit-block__value mb-0">{{ draftPost.title || '—' }}</p>
                </article>
              </div>
            </div>
          </template>
        </div>

        <div class="modal-footer content-modal-footer d-flex align-items-center justify-content-between">
          <!-- Кнопка "Вернуться к опубликованной версии" -->
          <button
              v-if="isPublished"
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="!canDiscard || isDiscarding || isSaving"
              @click="discardChanges"
              title="Подтянуть опубликованную версию"
          >
            <span v-if="!isDiscarding">Вернуться к опубликованной версии</span>
            <span v-else class="spinner-border spinner-border-sm"></span>
          </button>

          <div class="content-modal-actions" :class="{ 'ms-auto': !isPublished }">
            <button type="submit" class="btn btn-primary btn-sm" :disabled="isSaving">
              <span v-if="!isSaving">Сохранить</span>
              <span v-else class="spinner-border spinner-border-sm"></span>
            </button>
            <button type="button" class="btn btn-secondary" @click="$emit('close')">{{ standalone ? 'Назад' : 'Отмена' }}</button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import MarkdownIt from 'markdown-it'
import { sanitizeHtml } from '@/utils/sanitize'
import { withoutWordpressTextLengthLimits } from '@/utils/contentFormConstraints'

const md = new MarkdownIt({ html: false, linkify: true, typographer: true })

// Mermaid is loaded lazily (it's a heavy dep) and initialised once. securityLevel:'strict'
// makes mermaid sanitize diagram labels itself — important since the diagram source comes from
// generated content. The markdown preview keeps emitting mermaid as a <pre><code> block; we
// convert those to SVG after render (see renderMermaidBlocks).
let mermaidPromise = null
function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(mod => {
      const mermaid = mod.default
      // htmlLabels:false renders node labels as native SVG <text> instead of an HTML <div> in a
      // fixed-size <foreignObject>. The HTML path measures with SVG text but paints with a div
      // that obeys the browser's minimum font size / zoom, so the label can paint larger than the
      // box mermaid sized and get clipped. SVG text is measured and painted identically, so nodes
      // always size to fit. The flag only works at the top level (flowchart.htmlLabels is ignored).
      mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: 'default', htmlLabels: false })
      return mermaid
    })
  }
  return mermaidPromise
}

const MERMAID_DIAGRAM_RE =
  /^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram(-v2)?|erDiagram|journey|gantt|pie|mindmap|timeline|gitGraph|quadrantChart|requirementDiagram|xychart(-beta)?|sankey(-beta)?|block(-beta)?)\b/

export default {
  name: 'ContentModal',
  props: {
    mode: { type: String, default: 'edit' },
    contentData: { type: Object, default: () => ({ title: '', body: '' }) },
    standalone: { type: Boolean, default: false }
  },
  data() {
    return {
      draftPost: {
        title: '',
        body: '',
        metaDescription: '',
        leadParagraph: '',
        focusKw: '',
        slug: '',
        image: '',
        imageAlt: '',
        wpCategories: [],
        wpTags: []
      },
      listFieldText: {},

      streamType: '',
      form: null,
      isFormLoading: false,
      isSaving: false,
      editingFieldName: '',
      fieldEditSnapshot: null,
      fallbackFields: [
        { name: 'title', label: 'Название', type: 'string', required: true },
        { name: 'body', label: 'Body', type: 'markdown' }
      ],

      // "Опубликован?" и "есть изменения?"
      isDiscarding: false,
      isDirty: false,               // серверное/точное состояние (если удалось определить)
      initialWorkingSnapshot: {},   // локальный снимок формы при открытии, фоллбэк для "изменялся?"
      publishedSnapshot: null,      // если получится запросить

      dtoFields: new Set(['title','body','metaDescription','leadParagraph','slug','image','imageAlt','focusKw','wpCategories','wpTags'])
    }
  },
  computed: {
    contentId() {
      return this.contentData?.id
    },
    isPublished() {
      // считаем, что опубликован, если у контента есть externalId
      const ext = this.contentData && this.contentData.externalId
      return !!String(ext || '').trim()
    },
    fieldErrors() {
      const errs = {}
      const validateField = (f) => {
            const raw = this.draftPost?.[f.name]
            const jsonRaw = f.type === 'json' && typeof this.listFieldText[f.name] === 'string'
              ? this.listFieldText[f.name]
              : raw
            const v = f.type === 'json'
              ? this.parseJsonList(jsonRaw)
              : (typeof raw === 'string' ? raw.trim() : (raw ?? ''))
            if (f.required && Array.isArray(v) && v.length === 0) {
              return { ok: false, msg: 'Обязательное поле' }
            }
            if (f.minLength != null && Array.isArray(v) && v.length < f.minLength) {
              return { ok: false, msg: `Мин. ${f.minLength} элементов` }
            }
            if (f.maxLength != null && Array.isArray(v) && v.length > f.maxLength) {
              return { ok: false, msg: `Макс. ${f.maxLength} элементов` }
            }
            if (f.required && (!v || (typeof v === 'string' && v.length === 0))) {
              return { ok: false, msg: 'Обязательное поле' }
            }
            if (f.minLength != null && typeof v === 'string' && v.length < f.minLength) {
              return { ok: false, msg: `Мин. ${f.minLength} символов` }
            }
            if (f.maxLength != null && typeof v === 'string' && v.length > f.maxLength) {
              return { ok: false, msg: `Макс. ${f.maxLength} символов` }
            }
            if (f.pattern && typeof v === 'string' && v && !(new RegExp(f.pattern).test(v))) {
              return { ok: false, msg: 'Неверный формат' }
            }
            return { ok: true }
          }

      ;(this.form?.sections ?? []).forEach(s =>
          (s.fields ?? []).forEach(f => {
            const res = validateField(f)
            if (!res.ok) errs[f.name] = res.msg
          })
      )
      return errs
    },
    // Локально «изменялся» с момента открытия модалки
    locallyChanged() {
      return !this.shallowEqualByKeys(this.draftPost, this.initialWorkingSnapshot, Array.from(this.dtoFields))
    },
    // Кнопка "Вернуться..." доступна, если:
    // - контент опубликован;
    // - и (сервер подтвердил isDirty === true ИЛИ хотя бы локально есть изменения)
    canDiscard() {
      if (!this.isPublished) return false
      if (this.publishedSnapshot) {
        // если знаем опубликованный снапшот, сравниваем с текущей формой по полям формы
        return !this.shallowEqualByKeys(this.draftPost, this.publishedSnapshot, Array.from(this.dtoFields))
      }
      // иначе — серверный флаг или локальный флаг
      return this.isDirty || this.locallyChanged
    }
  },
  watch: {
    contentData: {
      immediate: true,
      handler(newVal) {
        const src = newVal || {}
        this.editingFieldName = ''
        this.fieldEditSnapshot = null
        this.applyFromObject(src)
        // снимок на момент открытия
        this.initialWorkingSnapshot = this.pickByKeys(this.draftPost, Array.from(this.dtoFields))
      }
    }
  },
  mounted() {
    this.ensureFormLoaded().then(() => {
      // после загрузки формы пробуем оценить "грязность"
      this.tryDetectDirty()
    })
    this.scheduleMermaid()
  },
  updated() {
    // Markdown previews are re-rendered into v-html islands on any data change; re-scan for
    // mermaid code blocks and turn the unprocessed ones into diagrams.
    this.scheduleMermaid()
  },
  methods: {
    // Coalesce multiple updates into a single post-DOM pass.
    scheduleMermaid() {
      if (this._mermaidScheduled) return
      this._mermaidScheduled = true
      this.$nextTick(() => {
        this._mermaidScheduled = false
        this.renderMermaidBlocks()
      })
    },

    async renderMermaidBlocks() {
      const root = this.$el
      if (!root || !root.querySelectorAll) return

      const blocks = []
      root.querySelectorAll('code.language-mermaid').forEach(c => blocks.push(c))
      // Fallback: a fenced block the writer left without the "mermaid" info string but whose
      // content is clearly a mermaid diagram.
      root.querySelectorAll('pre > code:not(.language-mermaid)').forEach(c => {
        if (MERMAID_DIAGRAM_RE.test((c.textContent || '').trim())) blocks.push(c)
      })

      const pending = blocks
        .map(code => code.closest('pre') || code)
        .filter(pre => pre.dataset.mermaidProcessed !== '1' && pre.dataset.mermaidProcessed !== 'error')
      if (!pending.length) return

      let mermaid
      try {
        mermaid = await loadMermaid()
      } catch (e) {
        console.warn('mermaid load failed', e)
        return
      }

      // securityLevel:'strict' forces SVG <text> labels (htmlLabels off), whose node boxes are
      // sized from a font measurement. If we render before the web font loads, boxes are sized for
      // the fallback font and the real (wider, esp. Cyrillic) glyphs overflow and get clipped. Wait
      // for fonts before measuring.
      if (document.fonts && document.fonts.ready) {
        try { await document.fonts.ready } catch (e) { /* fall through and render anyway */ }
      }

      for (const pre of pending) {
        const code = pre.matches('code') ? pre : pre.querySelector('code')
        const source = (code?.textContent || '').trim()
        if (!source) { pre.dataset.mermaidProcessed = '1'; continue }
        pre.dataset.mermaidProcessed = '1'
        const id = 'mmd-' + Math.random().toString(36).slice(2)
        try {
          const { svg } = await mermaid.render(id, source)
          const container = document.createElement('div')
          container.className = 'mermaid-diagram'
          container.innerHTML = svg
          pre.replaceWith(container)
        } catch (e) {
          // Keep the raw code block so the user still sees the source, and flag it once.
          pre.dataset.mermaidProcessed = 'error'
          console.warn('mermaid render failed', e)
        }
      }
    },

    async ensureFormLoaded() {
      if (!this.contentId) return
      this.isFormLoading = true
      try {
        const { data: type } = await axios.get(`/api/contents/${this.contentId}/stream-type`)
        this.streamType = type

        const { data } = await axios.get(`/api/streams/${type}/content-form`)
        this.form = withoutWordpressTextLengthLimits(data, type)

        const set = new Set()
        ;(this.form?.sections ?? []).forEach(s =>
            (s.fields ?? []).forEach(f => set.add(f.name))
        )
        if (set.size) this.dtoFields = set
        this.applyFromObject(this.contentData || {})
        this.initialWorkingSnapshot = this.pickByKeys(this.draftPost, Array.from(this.dtoFields))
      } catch (e) {
        this.form = null
      } finally {
        this.isFormLoading = false
      }
    },

    buildPayload() {
      if (this.form?.sections?.length) {
        const payload = {}
        this.syncJsonFieldsFromText()
        this.form.sections.forEach(s =>
            s.fields.forEach(f => {
              const raw = this.draftPost?.[f.name]
              payload[f.name] = f.type === 'json' ? this.parseJsonList(raw) : (raw ?? '')
            })
        )
        return payload
      }
      return {
        title: this.draftPost.title || '',
        body: this.draftPost.body || ''
      }
    },

    hasErrors() {
      return Object.keys(this.fieldErrors).length > 0
    },

    sectionTitle(section, index) {
      const raw = section?.label || section?.title || section?.name
      if (raw) return String(raw)
      return this.form?.sections?.length > 1 ? `Блок ${index + 1}` : ''
    },

    isBodyField(field) {
      return String(field?.name || '').toLowerCase() === 'body'
    },

    isTitleField(field) {
      return String(field?.name || '').toLowerCase() === 'title'
    },

    isEditingField(fieldName) {
      return this.editingFieldName === fieldName
    },

    displayFieldValue(field) {
      const value = this.draftPost?.[field.name]
      if (value == null || value === '') return '—'
      if (Array.isArray(value)) return value.length ? value.join(', ') : '—'
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },

    cloneFieldValue(field) {
      if (field?.type === 'json') {
        return {
          value: this.parseJsonList(this.draftPost?.[field.name]),
          listText: this.listFieldAsText(field.name)
        }
      }
      const value = this.draftPost?.[field.name]
      if (Array.isArray(value)) return [...value]
      if (value && typeof value === 'object') return JSON.parse(JSON.stringify(value))
      return value ?? ''
    },

    startEditField(field) {
      if (!field?.name) return
      this.editingFieldName = field.name
      this.fieldEditSnapshot = {
        name: field.name,
        type: field.type,
        value: this.cloneFieldValue(field)
      }
    },

    finishEditField(field) {
      if (field?.type === 'json') {
        this.commitListFieldText(field.name)
      }
      this.editingFieldName = ''
      this.fieldEditSnapshot = null
    },

    cancelEditField() {
      const snapshot = this.fieldEditSnapshot
      if (snapshot?.name) {
        if (snapshot.type === 'json') {
          this.draftPost[snapshot.name] = this.parseJsonList(snapshot.value?.value)
          this.listFieldText[snapshot.name] = snapshot.value?.listText || ''
        } else {
          this.draftPost[snapshot.name] = snapshot.value
        }
      }
      this.editingFieldName = ''
      this.fieldEditSnapshot = null
    },

    renderMarkdown(text) {
      const source = String(text || '').trim() || '—'
      return sanitizeHtml(md.render(source))
    },

    async onSave() {
      // просто пометим, что ошибки есть, и проскроллим — но не выходим
      const hasErr = this.hasErrors()
      if (hasErr) {
        try {
          const first = Object.keys(this.fieldErrors)[0]
          if (first) {
            const el = this.$el.querySelector(`[name="${first}"]`) || this.$el.querySelector('.text-danger')
            if (el && el.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          }
        } catch (_) {
          // no-op
        }
        // ВАЖНО: НЕ return !
      }

      this.isSaving = true
      try {
        const payload = this.buildPayload()
        await axios.put(`/api/contents/${this.contentId}`, payload)

        // обновляем локальный снимок
        this.initialWorkingSnapshot = this.pickByKeys(this.draftPost, Array.from(this.dtoFields))
        // и заново спросим "грязность" у бэка
        this.tryDetectDirty()

        this.$emit('save', { id: this.contentId, ...payload })
        this.$emit('close')
      } catch (e) {
        console.error('Не удалось сохранить контент', e)
      } finally {
        this.isSaving = false
      }
    },

    // --- Кнопка "Вернуться к опубликованной версии" ---
    async discardChanges() {
      if (!this.isPublished || !this.canDiscard || this.isDiscarding) return
      this.isDiscarding = true
      try {
        // Основной вариант — POST
        let published = null
        try {
          const { data } = await axios.post(`/api/contents/${this.contentId}/discard`, null)
          published = data || null
        } catch (e1) {
          //
        }

        // Если бек вернул опубликованную версию — применим её в форму
        if (published && typeof published === 'object') {
          this.applyFromObject(published)
        } else {
          // Если ничего не вернул — перечитаем текущую рабочую версию
          const { data } = await axios.get(`/api/contents/${this.contentId}`)
          this.applyFromObject(data || {})
        }

        // после отката обновим снимки и флаги
        this.initialWorkingSnapshot = this.pickByKeys(this.draftPost, Array.from(this.dtoFields))
        this.publishedSnapshot = null
        await this.tryDetectDirty()
      } catch (e) {
        console.error('Не удалось вернуть опубликованную версию', e)
      } finally {
        this.isDiscarding = false
      }
    },

    applyFromObject(obj) {
      if (!obj || typeof obj !== 'object') return
      // применяем только известные поля (из формы — если она есть)
      Object.keys(obj).forEach(k => {
        const value = obj[k]
        if (value == null) return
        const fieldType = this.getFieldType(k)
        if (fieldType === 'json') {
          this.draftPost[k] = this.parseJsonList(value)
          this.setListFieldTextFromValue(k, this.draftPost[k])
        } else {
          this.draftPost[k] = value
        }
      })
      const keys = this.form?.sections?.flatMap(s => s.fields?.map(f => f.name) || []) ?? Array.from(this.dtoFields)
      keys.forEach(k => {
        if (!(k in this.draftPost)) this.draftPost[k] = ''
        if (this.getFieldType(k) === 'json') {
          this.draftPost[k] = this.parseJsonList(this.draftPost[k])
          this.setListFieldTextFromValue(k, this.draftPost[k])
        }
      })
    },

    // --- Определение "грязности" ---
    async tryDetectDirty() {
      if (!this.isPublished || !this.contentId) {
        this.isDirty = false
        this.publishedSnapshot = null
        return
      }

      // 1) Попробуем серверный флаг
      try {
        const { data } = await axios.get(`/api/contents/${this.contentId}/dirty`)
        if (typeof data === 'boolean') {
          this.isDirty = data
          this.publishedSnapshot = null
          return
        }
      } catch (_) { /* no-op */ }

      // 2) Попробуем получить опубликованный снапшот для точного сравнения
      try {
        const { data } = await axios.get(`/api/contents/${this.contentId}/published`)
        if (data && typeof data === 'object') {
          this.publishedSnapshot = this.pickByKeys(data, Array.from(this.dtoFields))
          this.isDirty = !this.shallowEqualByKeys(this.draftPost, this.publishedSnapshot, Array.from(this.dtoFields))
          return
        }
      } catch (_) { /* no-op */ }

      // 3) Фоллбэк: сравниваем с локальным снимком (с момента открытия)
      this.isDirty = this.locallyChanged
    },

    // --- Утилиты сравнения ---
    pickByKeys(obj, keys) {
      const out = {}
      keys.forEach(k => {
        const value = obj?.[k]
        out[k] = Array.isArray(value) ? [...value] : (value ?? '')
      })
      return out
    },
    shallowEqualByKeys(a, b, keys) {
      for (const k of keys) {
        const av = (a?.[k] ?? '')
        const bv = (b?.[k] ?? '')
        if (this.serializeValue(av) !== this.serializeValue(bv)) return false
      }
      return true
    },
    parseJsonList(value) {
      if (Array.isArray(value)) return value.map(v => String(v).trim()).filter(Boolean)
      if (value == null) return []
      if (typeof value === 'string') {
        const raw = value.trim()
        if (!raw) return []
        try {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) return parsed.map(v => String(v).trim()).filter(Boolean)
        } catch (_) { /* no-op */ }
        return raw.split(/[\n,]/).map(v => v.trim()).filter(Boolean)
      }
      return [String(value).trim()].filter(Boolean)
    },
    setListFieldTextFromValue(fieldName, value) {
      this.listFieldText[fieldName] = this.parseJsonList(value).join('\n')
    },
    listFieldAsText(fieldName) {
      if (typeof this.listFieldText[fieldName] === 'string') return this.listFieldText[fieldName]
      const text = this.parseJsonList(this.draftPost?.[fieldName]).join('\n')
      this.listFieldText[fieldName] = text
      return text
    },
    updateListFieldFromText(fieldName, text) {
      this.listFieldText[fieldName] = text
      // Синхронизируем сразу, чтобы кнопки/сравнение "грязности" реагировали без blur.
      this.draftPost[fieldName] = this.parseJsonList(text)
    },
    commitListFieldText(fieldName) {
      this.draftPost[fieldName] = this.parseJsonList(this.listFieldText[fieldName])
    },
    syncJsonFieldsFromText() {
      (this.form?.sections ?? []).forEach(section => {
        (section.fields ?? []).forEach(field => {
          if (field.type !== 'json') return
          if (typeof this.listFieldText[field.name] !== 'string') return
          this.draftPost[field.name] = this.parseJsonList(this.listFieldText[field.name])
        })
      })
    },
    getFieldType(fieldName) {
      const fields = (this.form?.sections ?? []).flatMap(s => s.fields ?? [])
      const field = fields.find(f => f.name === fieldName)
      return field?.type || ''
    },
    serializeValue(value) {
      if (Array.isArray(value)) return JSON.stringify(value.map(v => String(v).trim()).filter(Boolean))
      if (value == null) return ''
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    }
  }
}
</script>

<style scoped>
.content-page-layout {
  min-height: 100%;
  padding: 8px 14px 12px;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 12px;
}

.modal-container.content-modal {
  width: min(96vw, 1200px);
  height: min(95vh, 920px);
  max-height: 95vh;
  border-radius: 16px;
  border: 1px solid #dfe7f5;
  background: linear-gradient(180deg, #fbfdff 0%, #f4f8ff 100%);
  box-shadow: 0 24px 52px rgba(15, 23, 42, 0.24);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-container.content-modal.content-modal--standalone {
  width: min(100%, 1320px);
  height: min(95vh, 980px);
  max-height: 95vh;
  margin: 0 auto;
}

.content-modal-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.content-modal-header {
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 10px 12px 0;
  min-height: 44px;
}

.content-modal-header--standalone {
  justify-content: space-between;
  padding-top: 10px;
}

.content-back-btn {
  height: 34px;
  border: 1px solid #dce6f6;
  border-radius: 10px;
  background: #fff;
  color: #364766;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.content-back-btn:hover {
  background: #f3f6fd;
  color: #1f2f4f;
}

.modal-title {
  position: absolute;
  left: 14px;
  top: 10px;
  margin: 0;
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: linear-gradient(180deg, #f2f7ff 0%, #eaf2ff 100%);
  color: #2f4f88;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 10px rgba(49, 94, 227, 0.08);
}

.modal-title--standalone {
  position: static;
}

.modal-close {
  width: 34px;
  height: 34px;
  border: 1px solid #dce6f6;
  border-radius: 10px;
  background: #fff;
  color: #6c7a95;
  font-size: 24px;
  line-height: 1;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.modal-close:hover {
  background: #f3f6fd;
  color: #2e3d59;
  transform: scale(1.02);
}

.modal-close:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.2);
}

/* оформление как в «Итог & Сохранение» */
.final-panel {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 12px 10px;
}
.content-modal-body::-webkit-scrollbar {
  width: 8px;
}

.content-modal-body::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background: #d1ddef;
}

.content-modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.content-section {
  border: 1px solid #e2ebf6;
  border-radius: 8px;
  background: #fbfdff;
  padding: 10px;
  margin-bottom: 10px !important;
}

.content-section__head {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.content-section__title {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d3e2fb;
  background: #edf4ff;
  color: #355b95;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 8px;
  line-height: 1;
}

.content-blocks-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.content-edit-block {
  min-width: 0;
  border: 1px solid #d9e4f8;
  border-radius: 8px;
  background: #fff;
  padding: 10px;
  box-shadow: 0 8px 18px rgba(24, 55, 100, 0.08);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.content-edit-block:hover {
  border-color: #b8cff3;
  transform: translateY(-1px);
}

.content-edit-block--editing {
  border-color: #8eb5f4;
  box-shadow: 0 0 0 2px #e2edff inset, 0 10px 22px rgba(44, 88, 160, 0.14);
}

.content-edit-block--error {
  border-color: #f2adb7;
}

.content-edit-block--body {
  grid-column: 1 / -1;
}

.content-edit-block--body .content-edit-block__head {
  margin-bottom: 2px;
}

.content-edit-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}

.content-edit-block__label-wrap {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.content-edit-block__label {
  color: #1e2d46;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.content-edit-block__edit {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 0;
}

.content-edit-block__editor {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.content-edit-block__textarea {
  min-height: 130px;
  resize: vertical;
}

.content-edit-block__textarea--markdown {
  min-height: 340px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 13px;
  line-height: 1.55;
}

.content-edit-block__actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.content-edit-block__value {
  color: #2c3f5f;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.content-edit-block--title .content-edit-block__value {
  color: #1f3150;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
}

.content-edit-block--title .form-control {
  font-size: 16px;
  font-weight: 600;
}

.content-edit-block__empty {
  color: #7d8ca3;
  font-size: 13px;
}

.content-edit-block__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.content-edit-block__tag {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d5e4fa;
  background: #eff5ff;
  color: #345a92;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  line-height: 1.2;
}

.content-edit-block__image-view {
  min-height: 96px;
  border: 1px solid #e5edf8;
  border-radius: 8px;
  background: #f7faff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.content-edit-block__markdown {
  min-height: 220px;
  max-height: none;
  color: #233653;
  font-size: 14px;
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.final-panel .form-label {
  margin: 0 !important;
  line-height: 1.1;
  font-size: 0.72rem;
  font-weight: 700;
  color: #4d5f82;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  padding-left: 1px;
}

.final-panel .form-control,
.final-panel .form-select {
  border-radius: 10px;
}

.final-panel .form-control:focus,
.final-panel .form-select:focus {
  border-color: #95b8ff;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.14);
}

.final-panel .form-label + input,
.final-panel .form-label + textarea,
.final-panel .form-label + select {
  margin-top: 4px;
}

.final-image {
  display: block;
  max-width: 100%;
  height: auto;
}

.content-image-preview {
  border-color: #dce7f7 !important;
  border-radius: 8px !important;
  background: #f6f9ff;
}

.markdown-preview :deep(*) {
  max-width: 100%;
}

.markdown-preview :deep(> :first-child) {
  margin-top: 0;
}

.markdown-preview :deep(h1),
.markdown-preview :deep(h2),
.markdown-preview :deep(h3),
.markdown-preview :deep(h4) {
  margin: 0.85em 0 0.45em;
  color: #1d2f4c;
  font-weight: 700;
  line-height: 1.25;
}

.markdown-preview :deep(h1) {
  font-size: 1.45rem;
}

.markdown-preview :deep(h2) {
  font-size: 1.25rem;
}

.markdown-preview :deep(h3) {
  font-size: 1.08rem;
}

.markdown-preview :deep(p) {
  margin: 0 0 0.75em;
}

.markdown-preview :deep(p:last-child),
.markdown-preview :deep(ul:last-child),
.markdown-preview :deep(ol:last-child) {
  margin-bottom: 0;
}

.markdown-preview :deep(ul),
.markdown-preview :deep(ol) {
  margin: 0 0 0.75em;
  padding-left: 1.25rem;
}

.markdown-preview :deep(blockquote) {
  margin: 0.8em 0;
  padding: 0.55rem 0.75rem;
  border-left: 3px solid #8eb5f4;
  background: #f4f8ff;
  color: #405778;
}

.markdown-preview :deep(table) {
  width: 100%;
  margin: 0.85rem 0;
  border-collapse: collapse;
  border: 1px solid #cfdced;
  background: #fff;
  font-size: 13px;
}

.markdown-preview :deep(th),
.markdown-preview :deep(td) {
  border: 1px solid #cfdced;
  padding: 0.45rem 0.6rem;
  text-align: left;
  vertical-align: top;
}

.markdown-preview :deep(th) {
  background: #edf4ff;
  color: #27486f;
  font-weight: 700;
}

.markdown-preview :deep(tbody tr:nth-child(even)) {
  background: #f8fbff;
}

.markdown-preview :deep(code) {
  border-radius: 4px;
  background: #eef3fb;
  color: #24466f;
  padding: 0.1rem 0.3rem;
  font-size: 0.92em;
}

.markdown-preview :deep(pre) {
  border: 1px solid #dbe6f6;
  border-radius: 8px;
  background: #f7faff;
  padding: 0.75rem;
  overflow-x: auto;
}

.markdown-preview :deep(pre code) {
  padding: 0;
  background: transparent;
}

.markdown-preview :deep(a) {
  color: #1f66c2;
  text-decoration: none;
}

.markdown-preview :deep(a:hover) {
  text-decoration: underline;
}

/* Rendered mermaid diagrams (the <pre><code class="language-mermaid"> block is replaced by
   a .mermaid-diagram div holding the SVG — see renderMermaidBlocks). */
.markdown-preview :deep(.mermaid-diagram) {
  margin: 0.75rem 0;
  padding: 0.75rem;
  border: 1px solid #dbe6f6;
  border-radius: 8px;
  background: #fbfdff;
  overflow-x: auto;
  text-align: center;
}

.markdown-preview :deep(.mermaid-diagram svg) {
  max-width: 100%;
  height: auto;
}

.content-modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 12px;
  border-top: 1px solid #e7edf8;
  background: #fff;
}

.content-modal-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.content-modal-footer .btn {
  min-width: 90px;
  padding: 0.3rem 0.65rem;
  font-size: 0.84rem;
}

@media (max-width: 768px) {
  .modal-backdrop {
    padding: 8px;
  }

  .modal-container.content-modal {
    width: 100%;
    height: 96vh;
    border-radius: 12px;
  }

  .modal-container.content-modal.content-modal--standalone {
    height: calc(100vh - 24px);
    max-height: calc(100vh - 24px);
  }

  .content-modal-header {
    padding: 8px 10px 0;
  }

  .content-modal-body {
    padding: 6px 10px 10px;
  }

  .content-modal-footer {
    padding: 8px 10px 10px;
    flex-wrap: wrap;
  }

  .content-blocks-grid {
    grid-template-columns: 1fr;
  }

  .content-edit-block__textarea--markdown {
    min-height: 260px;
  }
}
</style>
